import { useAppSelector } from '../../store.model';
import { toBase64 } from '../../../shared/util/base64';
import ControlUnitAPI from '../ControlUnitAPI';
import { getControlUnitsByBusAddressById } from '../selectors';
import { ControlUnitsByBusAddress } from '../../../service/device/util/getControlUnitsByBusAddress';
import { ComposableDeviceControlUnitMap } from '../../../service/device/CommanderAirConnectAPI/CommanderAirConnectAPI.model';
import { ShelfMappingResponse } from '../../../service/store/StoreService.model';

interface ControlUnitDeviceInfo {
  /** Device uuid used to trigger the control units fetch (base64 encoded). */
  uuid: string;
  /** Commander id required by the control units API. */
  commanderId: string;
  /** Store shelf mappings used as a fallback bus-address anchor. */
  shelfMappings?: Array<ShelfMappingResponse | null>;
  /** Composable device control units (cuaddr + cuid), the preferred anchor. */
  composableDevice?: { controlUnits?: ComposableDeviceControlUnitMap[] };
}

interface UseControlUnitsByBusAddressResult {
  controlUnitsByBusAddress: ControlUnitsByBusAddress;
  loading: boolean;
  error: boolean;
  refetch: () => void;
}

/**
 * Fetches a device's live control units and returns them indexed by ST bus
 * address (cuaddr). Unlike the raw {@link getControlUnitsByBusAddressById}
 * selector, this hook actually triggers the control units API call so the
 * underlying store is populated before the structure is derived.
 *
 * @usage
 * ```ts
 * const { controlUnitsByBusAddress, loading } =
 *   useControlUnitsByBusAddress(device);
 * ```
 */
export function useControlUnitsByBusAddress(
  device: ControlUnitDeviceInfo
): UseControlUnitsByBusAddressResult {
  const deviceId = toBase64(device.uuid);
  const commanderId = device.commanderId ?? '';

  const { isLoading, isFetching, isError, refetch } =
    ControlUnitAPI.useGetControlUnitsByDeviceQuery(
      { deviceId, commanderId },
      { skip: !commanderId }
    );

  const controlUnitsByBusAddress = useAppSelector((state) =>
    getControlUnitsByBusAddressById(state, {
      deviceId,
      composableControlUnits: device.composableDevice?.controlUnits,
      device: {
        shelfMappings: device.shelfMappings ?? [],
        commanderId,
      },
    })
  );

  return {
    controlUnitsByBusAddress,
    loading: isLoading || isFetching,
    error: isError,
    refetch,
  };
}
